import { CssBaseline, ThemeProvider } from '@mui/material';
import { uiDirection } from '../lib/i18n';
import { useQuery } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { fetchOrgSettings, readLastBrand } from '../lib/org-brand';
import { createAppTheme } from '../theme';

/**
 * Builds the whole Material 3 scheme from the company color (GET /settings).
 * Before login, the last company used on this device tints the sign-in page.
 */
export function OrgThemeProvider({ children }: { children: ReactNode }) {
  const { status, me } = useAuth();
  const q = useQuery({ queryKey: ['settings'], queryFn: fetchOrgSettings, enabled: status === 'authenticated', staleTime: 60_000 });
  const cached = readLastBrand();
  const serverColor = q.data?.primaryColor ?? q.data?.theme?.primaryColor ?? null;
  const cachedColor = cached?.primaryColor ?? null;
  const cacheMatchesOrganization = !me || cached?.slug === me.organization.slug;

  // Never swap an authenticated screen from the default blue to the org color.
  // Use the last confirmed color synchronously, and on a first visit keep the
  // app hidden until the settings request resolves.
  const color = serverColor ?? (cacheMatchesOrganization ? cachedColor : null);
  const waitingForBrand =
    (status === 'loading' && !cachedColor) ||
    (status === 'authenticated' && !serverColor && !(cacheMatchesOrganization && cachedColor) && q.isPending);
  const direction = uiDirection();
  const theme = useMemo(() => createAppTheme(color, direction), [color, direction]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {waitingForBrand
        ? <div aria-hidden="true" style={{ height: '100%', background: '#FFFFFF' }} />
        : children}
    </ThemeProvider>
  );
}
